import React, { useEffect, useMemo, useState } from "react";

const TARGET_OPTIONS = [
  { value: "CIBCMark", label: "CIBC Mark" },
  { value: "CIBCJoint", label: "CIBC Joint" },
  { value: "CIBCVisa", label: "CIBC Visa" },
  { value: "CIBCMell", label: "CIBC Mell" },
  { value: "CIBCMellVisa", label: "CIBC Mell Visa" },
  { value: "CdnTire", label: "Canadian Tire" },
];

const ACTIVE_STATUSES = ["queued", "running"];

const getBadgeClass = (status) => {
  switch (status) {
    case "running":
      return "badge badge-warning";
    case "queued":
      return "badge badge-secondary";
    case "succeeded":
      return "badge badge-success";
    case "failed":
      return "badge badge-danger";
    default:
      return "badge badge-light";
  }
};

const formatDate = (dateValue) => {
  if (!dateValue) return "";
  return new Date(dateValue).toLocaleString();
};

const formatDuration = (startedAt, finishedAt) => {
  if (!startedAt) return "";
  if (!finishedAt) return "In progress";

  const durationMs = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (durationMs < 1000) return `${durationMs} ms`;

  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

export default function ScrapeRuns() {
  const [selectedTarget, setSelectedTarget] = useState("CIBCMark");
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [startingRun, setStartingRun] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const activeRun = useMemo(
    () => runs.find((run) => ACTIVE_STATUSES.includes(run.status)),
    [runs]
  );

  const fetchRuns = async (showLoading = false) => {
    if (showLoading) {
      setLoadingRuns(true);
    }

    try {
      const response = await fetch("/api/scrapeRuns?limit=50", {
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Unable to load scrape runs");
      }
      setRuns(data);
      setError(null);
    } catch (fetchError) {
      setError(fetchError.message);
    } finally {
      if (showLoading) {
        setLoadingRuns(false);
      }
    }
  };

  useEffect(() => {
    fetchRuns(true);
  }, []);

  useEffect(() => {
    if (!activeRun) {
      return;
    }

    const interval = setInterval(() => {
      fetchRuns(false);
    }, 5000);

    return () => clearInterval(interval);
  }, [activeRun]);

  const triggerRun = async () => {
    setStartingRun(true);
    setError(null);
    setMessage(null);

    try {
      const response = await fetch("/api/scrapeRuns", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ target: selectedTarget }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Unable to start scrape run");
      }

      setMessage(`Started scrape run for ${selectedTarget}`);
      await fetchRuns(false);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setStartingRun(false);
    }
  };

  return (
    <div className="m-3">
      <h3>Scrape Runs</h3>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Run on Demand</h5>
          <div className="form-row align-items-end">
            <div className="col-md-4 mb-2">
              <label htmlFor="scrapeTarget">Target</label>
              <select
                id="scrapeTarget"
                className="form-control"
                value={selectedTarget}
                onChange={(event) => setSelectedTarget(event.target.value)}
                disabled={Boolean(activeRun) || startingRun}
              >
                {TARGET_OPTIONS.map((targetOption) => (
                  <option key={targetOption.value} value={targetOption.value}>
                    {targetOption.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4 mb-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={triggerRun}
                disabled={Boolean(activeRun) || startingRun}
              >
                {startingRun ? "Starting..." : "Run Scrape"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary ml-2"
                onClick={() => fetchRuns(true)}
                disabled={startingRun}
              >
                Refresh
              </button>
            </div>
          </div>
          {activeRun && (
            <div className="mt-2 text-muted">
              A scrape run is currently active. Please wait for it to complete before starting another.
            </div>
          )}
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Current Run</h5>
          {!activeRun && <div className="text-muted">No active scrape run.</div>}
          {activeRun && (
            <div>
              <div className="mb-1">
                <strong>{activeRun.target}</strong>
              </div>
              <div className="mb-1">
                <span className={getBadgeClass(activeRun.status)}>{activeRun.status}</span>
              </div>
              <div className="small text-muted">Started: {formatDate(activeRun.startedAt || activeRun.createdAt)}</div>
              <div className="small text-muted">Requested by: {activeRun.requestedBy}</div>
            </div>
          )}
        </div>
      </div>

      <h5>Recent Runs</h5>
      {loadingRuns && (
        <div className="lds-ellipsis" style={{ width: "100%", marginLeft: "50%" }}>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      )}

      {!loadingRuns && (
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Created</th>
              <th>Target</th>
              <th>Status</th>
              <th>Requested By</th>
              <th>Duration</th>
              <th>Exit Code</th>
              <th>Error</th>
              <th>Output</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr>
                <td colSpan={7} className="text-muted">
                  No scrape runs yet.
                </td>
              </tr>
            )}
            {runs.map((run) => (
              <tr key={run._id}>
                <td>{formatDate(run.createdAt)}</td>
                <td>{run.target}</td>
                <td>
                  <span className={getBadgeClass(run.status)}>{run.status}</span>
                </td>
                <td>{run.requestedBy}</td>
                <td>{formatDuration(run.startedAt, run.finishedAt)}</td>
                <td>{run.exitCode ?? ""}</td>
                <td style={{ maxWidth: "380px", whiteSpace: "pre-wrap" }}>
                  {run.error || ""}
                </td>
                <td style={{ maxWidth: "380px", whiteSpace: "pre-wrap" }}>
                  {run.outputTail || ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
